// ライブラリのデータ層: Google Drive（ユーザー自身の Immerso フォルダ）から catalog を読み、
// 項目（bundle＋メディア）を端末へダウンロードする。端末内ファイルの取り込み・フォルダ管理も担う。
// （旧 R2 直アクセスは廃止。クラウド配布はメディア＝Drive BYO、辞書＝公開バケット[dicts.js] に二分。）
import { db, kvGet, kvSet } from './db.js'
import { saveMediaStream, hasMedia, deleteMedia } from './opfs.js'
import * as gdrive from './gdrive.js'
import { isPurchased } from './entitlements.js'

// 端末に取り込み済みの hash 集合（一覧で「DL済み」を出すため）。
export async function downloadedHashes() {
  const keys = await (await db()).getAllKeys('items')
  return new Set(keys)
}

// ===== Google Drive（受け取り） =====
// PC が Drive の Immerso フォルダに上げた catalog（Drive形＝mediaFile/bundleFile/durationSec）を
// 一覧用配列へ。tombstone(deleted)は除外。
export function gdriveCatalogItems(catalog) {
  const items = catalog.items || {}
  return Object.keys(items)
    .filter((h) => !items[h].deleted)
    .map((h) => {
      const e = items[h]
      return {
        hash: h,
        name: e.name || '(無題)',
        kind: e.kind || 'audio',
        duration: e.durationSec || 0,
        hasSubtitles: !!e.hasSubtitles,
        mediaFile: e.mediaFile || null,
        bundleFile: e.bundleFile || null,
        sizeBytes: e.sizeBytes || 0,
        ver: e.ver || 0,
        folderId: e.folderId || null,
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
}

// Drive から1項目をダウンロード。保存先は tracks/state/OPFS/items。
// entry は gdriveCatalogItems() の1要素。origin='gdrive' で記録（再生はプラン無関係）。
export async function downloadGDriveItem(entry, onProgress) {
  const hash = entry.hash
  const report = (phase, loaded = 0, total = 0) =>
    onProgress && onProgress({ phase, loaded, total })

  // 1) バンドル（{ metaContent, files }）
  report('bundle')
  const bundleResp = await gdrive.getResponseByName(entry.bundleFile)
  const bundle = await bundleResp.json()
  const metaContent = bundle.metaContent || {}
  const files = bundle.files || {}

  const d = await db()
  const tx = d.transaction(['tracks', 'state'], 'readwrite')
  for (const name of Object.keys(files)) {
    if (name === 'state.json') {
      try {
        tx.objectStore('state').put(JSON.parse(files[name]), hash)
      } catch {
        /* 壊れた state は無視 */
      }
    } else {
      tx.objectStore('tracks').put(files[name], `${hash}/${name}`)
    }
  }
  await tx.done

  // 2) メディア本体をストリーム保存（Drive のファイル名＝人間可読）
  let mediaExt = null
  if (entry.mediaFile) {
    mediaExt = (entry.mediaFile.split('.').pop() || '').toLowerCase()
    report('media', 0, entry.sizeBytes || 0)
    const resp = await gdrive.getResponseByName(entry.mediaFile)
    const total = Number(resp.headers.get('content-length')) || entry.sizeBytes || 0
    await saveMediaStream(hash, mediaExt, resp.body, total, (loaded, tt) => report('media', loaded, tt))
  }

  // 3) 項目メタ（origin='gdrive'＝再生はプラン無関係）
  await d.put('items', {
    hash,
    name: entry.name,
    kind: entry.kind,
    duration: entry.duration || metaContent.duration || 0,
    hasSubtitles: entry.hasSubtitles,
    mediaExt,
    origin: 'gdrive',
    folderId: entry.folderId || metaContent.folderId || null, // catalog のフォルダ所属を反映
    metaContent,
    fileNames: Object.keys(files),
    syncVer: entry.ver || 0,
    downloadedAt: new Date().toISOString(),
    freeTier: !isPurchased(), // 取得時タグ: 無料枠で取得=true / 購入中に取得=false（ダウングレード時のロック判定）
  })
  report('done')
  return true
}

// 端末からの削除（メタ・トラック・状態・メディアを全部消す）。
export async function removeLocalItem(hash) {
  const d = await db()
  await d.delete('items', hash)
  await d.delete('state', hash)
  // tracks は `${hash}/...` の複合キー。前方一致で全削除。
  const tx = d.transaction('tracks', 'readwrite')
  let cur = await tx.store.openCursor()
  while (cur) {
    if (String(cur.key).startsWith(hash + '/')) cur.delete()
    cur = await cur.continue()
  }
  await tx.done
  await deleteMedia(hash)
}

// 取り込み済み項目の一覧（オフライン時/再生用）。
export async function localItems() {
  const all = await (await db()).getAll('items')
  return all.sort((a, b) => a.name.localeCompare(b.name, 'ja'))
}

// 出どころで絞った一覧。'gdrive'=Drive 由来 / 'local'=端末で直接取り込み。
export async function itemsByOrigin(origin) {
  const all = await (await db()).getAll('items')
  return all
    .filter((m) => (m.origin || 'gdrive') === origin)
    .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
}

// 動画判定（MIME 優先、無ければ拡張子）。
const VIDEO_EXT = new Set(['mp4', 'm4v', 'webm', 'mov', 'ogv', 'mkv'])
function isVideoFile(file, ext) {
  if (file.type) return file.type.startsWith('video/')
  return VIDEO_EXT.has(ext)
}

// メディアの長さ(秒)を端末で取得（metadata だけ読む）。失敗時は 0。
function probeDuration(file, kind) {
  return new Promise((resolve) => {
    const el = document.createElement(kind === 'video' ? 'video' : 'audio')
    const url = URL.createObjectURL(file)
    el.preload = 'metadata'
    const done = (d) => { URL.revokeObjectURL(url); resolve(Number.isFinite(d) && d > 0 ? d : 0) }
    el.onloadedmetadata = () => done(el.duration)
    el.onerror = () => done(0)
    el.src = url
  })
}

// 端末のファイルをローカルライブラリへ取り込む（クラウドを通さない＝プラン無関係で使える）。
// メディアは OPFS、項目メタは items ストアへ origin:'local' で保存。戻り値 hash。
export async function importLocalFile(file, onProgress) {
  const d = await db()
  const ext = (file.name.match(/\.([^.\s]+)$/)?.[1] || '').toLowerCase()
  const name = file.name.replace(/\.[^.]+$/, '') || '(無題)'
  const kind = isVideoFile(file, ext) ? 'video' : 'audio'
  // local- 接頭辞でクラウドの contentHash（"p1:..."）と衝突しないキーにする。
  const hash = 'local-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7)
  const duration = await probeDuration(file, kind)
  await saveMediaStream(hash, ext, file.stream(), file.size || 0, onProgress)
  await d.put('items', {
    hash,
    name,
    kind,
    mediaExt: ext,
    origin: 'local',
    duration,
    hasSubtitles: false,
    fileNames: [],
    importedAt: new Date().toISOString(),
    freeTier: !isPurchased(), // 取得時タグ: 無料枠で取得=true / 購入中に取得=false（ダウングレード時のロック判定）
  })
  return hash
}

// 自動分割のセグメント(cue)を「autoseg.json」に別保存（添付字幕とは別枠）。
// プレーヤは「メイン字幕=なし」のときだけこれを帯/ループに使う。字幕があればそちら優先。
export async function saveLocalSegments(hash, cues) {
  if (!cues || !cues.length) return
  const d = await db()
  await d.put('tracks', JSON.stringify({ cues }), `${hash}/autoseg.json`)
  const it = await d.get('items', hash)
  if (it) {
    it.segmentCount = cues.length
    await d.put('items', it)
  }
}

// 字幕ファイル(SRT/VTT)を項目に「名前付きトラック」として取り込む（何個でも追加可）。
// ・cue は ${hash}/<id>.json に保存し、tracks.json（トラックプール）へ {id,label,file} を追記。
//   → プレーヤの字幕選択(loadTrackList)にファイル名で並び、メイン/サブを自由に付け替えられる。
// ・slot は「最初の割り当て」だけを決める（itemPrefs.mainId/subId）。あとからプレーヤで変更可。
// ・autoseg.json はプールに入れない＝選択肢に出ない（メイン/サブ両方なしのときだけ帯に使う）。
// 戻り値 { ok, count, label } / { ok:false }。
export async function attachSubtitleFile(hash, file, slot) {
  const raw = await file.text()
  const { parseSubtitles } = await import('./subtitles.js')
  const cues = parseSubtitles(raw)
  if (!cues.length) return { ok: false }
  const d = await db()
  const id = 'subtrk-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7)
  const fileName = `${id}.json`
  await d.put('tracks', JSON.stringify({ cues }), `${hash}/${fileName}`)

  // トラックプール(tracks.json)へ追記。無ければ新規作成。
  let pool = { tracks: [] }
  const rawPool = await d.get('tracks', `${hash}/tracks.json`)
  if (rawPool) { try { pool = JSON.parse(rawPool) } catch { /* 壊れていたら作り直す */ } }
  if (!Array.isArray(pool.tracks)) pool.tracks = []
  const label = file.name.replace(/\.[^.]+$/, '') || file.name || 'Subtitle'
  pool.tracks.push({ id, label, file: fileName })
  await d.put('tracks', JSON.stringify(pool), `${hash}/tracks.json`)

  // 最初の割り当て（メイン/サブ）。あとからプレーヤの字幕選択で変更できる。
  // ※サブに割り当てる場合、メイン未指定だと pickMainKey が list[0]（=今入れた字幕）を
  //   勝手に拾ってメインにも出てしまう。メイン未指定なら明示的に「なし」にして二重表示を防ぐ。
  const { getItemPrefs, setItemPrefs } = await import('./playerprefs.js')
  if (slot === 'sub') {
    const cur = await getItemPrefs(hash)
    const patch = { subId: id }
    if (!cur.mainId) patch.mainId = '__none__'
    await setItemPrefs(hash, patch)
  } else {
    await setItemPrefs(hash, { mainId: id })
  }

  const it = await d.get('items', hash)
  if (it) {
    it.hasSubtitles = true
    if (!Array.isArray(it.fileNames)) it.fileNames = []
    for (const n of [fileName, 'tracks.json']) if (!it.fileNames.includes(n)) it.fileNames.push(n)
    await d.put('items', it)
  }
  return { ok: true, count: cues.length, label }
}

// ===== フォルダ（入れ子可）。Drive の folders.json と whole-doc LWW で双方向同期（段2）。=====
// 構造: kv['localFolders'] = [{ id, name, parentId, order }]。更新時刻は kv['foldersUpdatedAt']（同期の新旧判定）。
// 項目側は items.folderId で所属。フォルダ変更のたびに updatedAt を打ち、同期で「新しい方」を採用。
const LOCAL_FOLDERS_KV = 'localFolders'
const FOLDERS_UA_KV = 'foldersUpdatedAt'

export async function localFolders() {
  const v = await kvGet(LOCAL_FOLDERS_KV)
  return Array.isArray(v) ? v : []
}
export async function foldersUpdatedAt() {
  return (await kvGet(FOLDERS_UA_KV)) || ''
}
async function bumpFolders(folders) {
  const ua = new Date().toISOString()
  await kvSet(LOCAL_FOLDERS_KV, folders)
  await kvSet(FOLDERS_UA_KV, ua)
}
// 同期：リモートの folders.json でローカルを置き換え（updatedAt も保持）。
export async function replaceFoldersFromSync(foldersArr, ua) {
  await kvSet(LOCAL_FOLDERS_KV, Array.isArray(foldersArr) ? foldersArr : [])
  await kvSet(FOLDERS_UA_KV, ua || new Date().toISOString())
}

export async function createLocalFolder(name, parentId = null) {
  const folders = await localFolders()
  const id = 'lf-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7)
  const order = folders.length ? Math.max(...folders.map((f) => f.order || 0)) + 1 : 0
  folders.push({ id, name: name || '', parentId: parentId || null, order })
  await bumpFolders(folders)
  return id
}

export async function renameLocalFolder(id, name) {
  const folders = await localFolders()
  const f = folders.find((x) => x.id === id)
  if (!f) return
  f.name = name
  await bumpFolders(folders)
}

// フォルダ削除: 直下のサブフォルダと項目はルートへ戻す（ファイル自体は消さない）。
export async function deleteLocalFolder(id) {
  let folders = await localFolders()
  for (const f of folders) if ((f.parentId || null) === id) f.parentId = null
  folders = folders.filter((f) => f.id !== id)
  await bumpFolders(folders)
  const d = await db()
  const all = await d.getAll('items')
  for (const it of all) {
    if ((it.folderId || null) === id) {
      it.folderId = null
      await d.put('items', it)
    }
  }
}

// フォルダの移動（親を変更）。自分自身/自分の子孫を親に指定するのは無視（循環防止）。
export async function moveLocalFolder(id, parentId) {
  if (id === (parentId || null)) return
  const folders = await localFolders()
  const byId = new Map(folders.map((f) => [f.id, f]))
  // parentId が id の子孫なら NG
  let cur = parentId || null
  const seen = new Set()
  while (cur && !seen.has(cur)) {
    if (cur === id) return // 子孫に動かそうとしている
    seen.add(cur)
    cur = byId.get(cur)?.parentId || null
  }
  const f = byId.get(id)
  if (!f) return
  f.parentId = parentId || null
  await bumpFolders(folders)
}

// 項目を指定フォルダへ移動（null=ルート）。
export async function moveLocalItem(hash, folderId) {
  const d = await db()
  const it = await d.get('items', hash)
  if (!it) return
  it.folderId = folderId || null
  await d.put('items', it)
}

export { hasMedia }
